<!-- @format -->

<template>
	<PcDialog
		v-model="resetPasswordStore.dialogVisible"
		title="设置密码"
		width="40%"
		@on-confirm="onConfirm"
		@on-cancel="onCancel"
	>
		<el-form ref="pwdFormRef" :model="formPsdParam" :rules="formRules">
			<el-row :gutter="30">
				<el-col :span="24">
					<el-form-item label="设置密码" prop="newPassword">
						<el-input v-model="formPsdParam.newPassword" autocomplete="off" show-password placeholder="请输入">
						</el-input>
					</el-form-item>
				</el-col>
				<el-col :span="24">
					<el-form-item label="重复密码" prop="repeatPassword">
						<el-input v-model="formPsdParam.repeatPassword" autocomplete="off" show-password placeholder="请输入">
						</el-input>
					</el-form-item>
				</el-col>
			</el-row>
		</el-form>
	</PcDialog>
</template>
<script setup lang="ts">
	import { useResetPasswordStoreWithOut, useGlobalStoreWithOut } from '@power/runtime-vue3';
	import { ElMessage } from 'element-plus';
	import { PcDialog } from '@/components';
	import { changePassword, getDefaultUserSecurity } from '@/api/user';
	import { getPublicKey } from '@/api/login';
	import { encodeStr } from '@/utils/encrypt';

	const globalStore = useGlobalStoreWithOut();

	const formPsdParam = ref<any>({});

	const pwdFormRef = ref();

	const resetPasswordStore = useResetPasswordStoreWithOut();

	const userSecurityRegx: any = {
		1: /[a-z]/g,
		2: /[A-Z]/g,
		3: /\d+/g,
		4: /((?=[\x21-\x7e]+)[^A-Za-z0-9])/g,
	};

	const userSecurity: any = ref({
		complexity: [],
		maxLength: 0,
		minLength: 0,
	});

	const userSecurityText: any = ref({
		1: '小写字母',
		2: '大写字母',
		3: '数字',
		4: '特殊符号',
	});

	const complexityArray = ref([
		{
			nameKey: '小写字母',
			valueKey: '1',
		},
		{
			nameKey: '大写字母',
			valueKey: '2',
		},
		{
			nameKey: '数字',
			valueKey: '3',
		},
		{
			nameKey: '特殊符号',
			valueKey: '4',
		},
	]);

	const restoreDefaultPassW = ref('');

	const formRules = {
		newPassword: [
			{ required: true, message: '请输入设置密码', trigger: 'blur' },
			{
				validator: (rule, value, callback) => {
					rule.userSecurity = userSecurity.value;
					validatePassword(rule, value, callback);
				},
				trigger: ['change', 'blur'],
			},
		],
		repeatPassword: [
			{ required: true, message: '请输入重复密码', trigger: 'blur' },
			{
				validator: (rule, value, callback) => {
					validateRepeatPassword(rule, value, callback);
				},
				trigger: 'blur',
			},
		],
	};

	const validateRepeatPassword = (rule, value, callback) => {
		const reg = /(^\s+)|(\s+$)|\s+/g;
		if (restoreDefaultPassW.value !== '') return;
		if (value === '') {
			callback(new Error('请再次输入密码'));
		} else if (value !== formPsdParam.value.newPassword) {
			callback(new Error('两次输入密码不一致'));
		} else if (reg.test(value)) {
			callback(new Error('请勿携带空格'));
		} else {
			callback();
		}
	};

	const validatePassword = (rule, value, callback) => {
		if (!value) {
			callback('请输入设置密码');
			return;
		}
		const reg = /(^\s+)|(\s+$)|\s+/g;
		const userSecurity = rule.userSecurity;
		// 未设置复杂度
		if (!userSecurity) {
			if (reg.test(value)) {
				callback(new Error('请勿携带空格'));
			} else {
				callback();
			}
			return;
		}

		// 最小长度 ,最大长度
		if (value.length < userSecurity.minLength || value.length > userSecurity.maxLength) {
			const msg = `最小长度${userSecurity.minLength};` + `最大长度${userSecurity.maxLength}`;
			callback(new Error(msg));
			return;
		}

		const flag: any = [];
		for (const l in userSecurityRegx) {
			if (value.match(userSecurityRegx[l]) !== null) {
				flag.push(l);
			}
		}
		const complexity = userSecurity.complexity || [];
		const arrMsg: any = [];
		for (let i = 0; i < complexity.length; i++) {
			arrMsg.push(userSecurityText.value[parseInt(complexity[i])]);
		}
		const isContained = (a, b) => {
			// a和b其中一个不是数组，直接返回false
			if (!(a instanceof Array) || !(b instanceof Array)) return false;
			const len = b.length;
			// a的长度小于b的长度，直接返回false
			if (a.length < len) return false;
			for (let i = 0; i < len; i++) {
				// 遍历b中的元素，遇到a没有包含某个元素的，直接返回false
				if (!a.includes(b[i])) return false;
			}
			// 遍历结束，返回true
			return true;
		};
		// 正则匹配
		if (isContained(flag, [...userSecurity.complexity]) == false) {
			const msg = `必须包含${arrMsg.join(',')}`;
			callback(new Error(msg));
		} else {
			callback();
		}
	};

	const getValidate = () => {
		getDefaultUserSecurity()
			.then((response) => {
				const data: any = response;
				if (!data) {
					userSecurity.value = {};
					return;
				}

				const newComplexity: any = [];
				const resData: any = data.complexity.split(',');
				complexityArray.value.map((item) => {
					resData.map((val) => {
						if (item.nameKey == val) {
							newComplexity.push(item.valueKey);
						}
					});
				});
				userSecurity.value.complexity = newComplexity.sort();
				userSecurity.value.maxLength = data.maxLength;
				userSecurity.value.minLength = data.minLength;
			})
			.catch(() => {});
	};

	const onConfirm = async (callback) => {
		try {
			const isVaild = await pwdFormRef.value?.validate();
			if (isVaild) {
				if (formPsdParam.value.newPassword != formPsdParam.value.repeatPassword) {
					ElMessage.error('两次密码不一致');
				} else {
					const res1: any = await getPublicKey();
					const res: any = await changePassword({
						userIds: [globalStore.userInfo.user.id],
						reset: 1,
						newPassword: await encodeStr(formPsdParam.value.newPassword, res1.data.publicKey),
						repeatPassword: await encodeStr(formPsdParam.value.repeatPassword, res1.data.publicKey),
						keyId: res1.data.keyId,
					});
					if (res.code === 200) {
						ElMessage.success('设置成功');
						resetPasswordStore.hideDialog();
					}
				}
			}
		} catch (error) {
		} finally {
			callback && callback();
		}
	};

	const onCancel = (callback) => {
		resetPasswordStore.hideDialog();
		callback && callback();
	};

	onMounted(() => {
		if (resetPasswordStore.dialogVisible) {
			getValidate();
			resetPasswordStore.showDialog();
		}
	});
</script>
